import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Superscript from "@tiptap/extension-superscript"
import Subscript from "@tiptap/extension-subscript"
import Highlight from "@tiptap/extension-highlight"
import TextAlign from "@tiptap/extension-text-align"
import { TextStyle, Color } from "@tiptap/extension-text-style"
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table"
import Image from "@tiptap/extension-image"
import { LinkPanelHighlight, linkPanelHighlightDecorationKey } from "./link_panel_highlight"
import { ArticleAlignedImage } from "./article_aligned_image"

export { linkPanelHighlightDecorationKey }

/** Yellow highlight; matches toolbar swatch. Used for Mod-Shift-H (TipTap default shortcut). */
export const DEFAULT_TIPTAP_KEYBOARD_HIGHLIGHT_COLOR = "#fef08a"

function imageExtension(imageResize) {
  if (!imageResize) return Image.configure({ allowBase64: false })

  return ArticleAlignedImage.configure({
    allowBase64: false,
    resize: {
      enabled: true,
      directions: ["top-left", "top-right", "bottom-left", "bottom-right"],
      minWidth: 80,
      minHeight: 60,
      alwaysPreserveAspectRatio: true,
    },
  })
}

const TipTapHighlight = Highlight.extend({
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-h": () =>
        this.editor.commands.toggleHighlight({ color: DEFAULT_TIPTAP_KEYBOARD_HIGHLIGHT_COLOR }),
    }
  },
}).configure({ multicolor: true })

/**
 * Default TipTap StarterKit options for this app (headings, links, etc.).
 * Pass `starterKitConfigure` to {@link defaultTipTapExtensions} or {@link createTipTapEditor} to merge overrides.
 */
export const DEFAULT_TIPTAP_STARTER_KIT = {
  heading: { levels: [1, 2, 3] },
  link: {
    openOnClick: false,
    HTMLAttributes: {
      rel: "nofollow noopener noreferrer",
      target: "_blank",
    },
  },
}

/**
 * Default TipTap extension stack for this app (StarterKit, tables, highlight, etc.).
 *
 * @param {object} [options]
 * @param {import("@tiptap/core").Extension[]} [options.additionalExtensions] — appended after the default stack (e.g. custom ProseMirror plugins)
 * @param {object} [options.starterKitConfigure] — shallow-merged into `StarterKit.configure(...)` after {@link DEFAULT_TIPTAP_STARTER_KIT}
 */
export function defaultTipTapExtensions({
  additionalExtensions = [],
  starterKitConfigure = {},
  imageResize = false,
} = {}) {
  return [
    StarterKit.configure({
      ...DEFAULT_TIPTAP_STARTER_KIT,
      ...starterKitConfigure,
    }),
    Superscript,
    Subscript,
    TipTapHighlight,
    TextStyle,
    Color,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Table.configure({ resizable: false }),
    TableRow,
    TableCell,
    TableHeader,
    LinkPanelHighlight,
    imageExtension(imageResize),
    ...additionalExtensions,
  ]
}

/**
 * Default TipTap `Editor` constructor options used by {@link createTipTapEditor}.
 */
export const DEFAULT_TIPTAP_EDITOR_OPTIONS = {
  injectCSS: true,
}

/**
 * Create a TipTap {@link Editor} with {@link defaultTipTapExtensions} and shared defaults.
 *
 * @param {object} options
 * @param {HTMLElement} options.element — mount node (required)
 * @param {string} [options.content] — initial HTML
 * @param {import("@tiptap/core").EditorProps} [options.editorProps]
 * @param {import("@tiptap/core").Extension[]} [options.additionalExtensions] — passed to {@link defaultTipTapExtensions}
 * @param {object} [options.starterKitConfigure] — passed to {@link defaultTipTapExtensions}
 * @param {import("@tiptap/core").Extension[]} [options.extensions] — if set, replaces the entire extension list (ignores additionalExtensions / starterKitConfigure)
 * @param {boolean} [options.injectCSS]
 * @param {object} [rest] — any other TipTap `Editor` constructor options (e.g. `autofocus`, `editable`)
 */
export function createTipTapEditor(options) {
  const {
    element,
    content = "",
    editorProps = {},
    additionalExtensions = [],
    starterKitConfigure = {},
    extensions: extensionsOverride,
    injectCSS = DEFAULT_TIPTAP_EDITOR_OPTIONS.injectCSS,
    imageResize = false,
    ...rest
  } = options

  if (!element) {
    throw new Error("createTipTapEditor: element is required")
  }

  const extensions =
    extensionsOverride ??
    defaultTipTapExtensions({ additionalExtensions, starterKitConfigure, imageResize })

  return new Editor({
    element,
    content,
    extensions,
    injectCSS,
    editorProps,
    ...rest,
  })
}
